import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import { Send, Play, Pause, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TextToImageDialog, VoiceSynthesisDialog, ImageSelectionDialog } from '../components/common'
import { getImageSizes, getVoices, generateImage, generateTTS, createVideoTask, saveSceneList as saveSceneListApi, resetCurrentProjectId } from '../utils/api'
import Toast from '../components/Toast'
import { PureFrontendVideoPlayer } from '../utils/PureFrontendVideoPlayer'

// 引入状态机
import { useVideoPageStateMachine } from '../hooks/useVideoPageStateMachine'
import { PageState } from '../types/VideoPageStateMachine'
import { chatCompletion } from '../utils/api'

// 分镜状态枚举
enum SceneStatus {
  LOADING = 'loading',           // 正在接收流式数据
  READY = 'ready',               // 数据完整，可编辑
  GENERATING_MEDIA = 'generating_media', // 正在生成图片/音频
  ERROR = 'error',               // 生成失败
  SELECTING = 'selecting'        // 选择中状态
}

// 游标位置接口
interface CursorPosition {
  startTime: number; // 开始时间，0-10s
  endTime: number;   // 结束时间，0-10s
}

// 优化后的分镜数据结构
interface SceneItem {
  id: string;
  desc: string;
  prompt: string;
  dialogue: string;
  images: string[];
  videoUrl: string;
  audioUrl: string;
  status: SceneStatus;

  // UI相关状态
  cursorPosition?: CursorPosition;
  waveformData?: number[];
  audioPlayState?: boolean;
  videoLoading?: boolean;
}

// VideoPage ref 接口定义
export interface VideoPageRef {
  reloadPlayerAndPlayFromScene: (sceneIndex?: number) => void;
  getSceneList: () => SceneItem[];
}

// 生成唯一ID
const generateUniqueId = () => {
  return Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

const VideoPage = forwardRef<VideoPageRef>((_, ref) => {
  const { t } = useTranslation()
  const [inputContent, setInputContent] = useState('')
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  // 初始化状态机
  const stateMachine = useVideoPageStateMachine()

  // 兼容层 - 将状态机映射到原有变量（保证功能不缺失）
  //  const compatShowTutorial = stateMachine.getCompatShowTutorial() // 未使用变量，暂时注释掉


  useEffect(() => {
    // 当状态机进入INITIAL状态时，销毁播放器实例
    if (stateMachine.pageState === 'initial') {
      console.log('🎬 [useEffect] 状态机进入INITIAL状态，销毁播放器实例');
      if (videoPlayerInstance.current) {
        videoPlayerInstance.current.destroy();
        videoPlayerInstance.current = null;
      }
    }
  }, [stateMachine.pageState])

  // 主数据源 - 统一的分镜列表
  const [sceneList, setSceneList] = useState<SceneItem[]>([])
  // 视频标题
  const [title, setTitle] = useState<string>("")

  // SSE reader ref for cancellation
  const sseReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)

  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  // 视频播放器引用
  const videoPlayerContainer = useRef<HTMLDivElement>(null)
  const videoPlayerInstance = useRef<PureFrontendVideoPlayer | null>(null)

  // 游标位置相关Ref - 用于避免状态异步问题
  const latestCursorPositionsRef = React.useRef<{ [key: string]: CursorPosition }>({});
  const cursorSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const isDraggingRef = React.useRef<boolean>(false); // 拖拽状态标志
  const skipNextSaveRef = React.useRef<boolean>(false); // 跳过下次保存标记

  // API 保存频控相关Ref
  const apiSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // 音频实例管理
  const audioInstancesRef = React.useRef<{ [key: string]: HTMLAudioElement | null }>({});

  // // UI显示用的游标位置（实时更新，用于显示）
  // const [uiCursorPositions, setUiCursorPositions] = React.useState<{ [key: string]: CursorPosition }>({ });

  // Dialog state - 保留弹窗功能
  const [isTextToImageDialogOpen, setIsTextToImageDialogOpen] = useState(false)
  const [isVoiceSynthesisDialogOpen, setIsVoiceSynthesisDialogOpen] = useState(false)
  const [isImageSelectionDialogOpen, setIsImageSelectionDialogOpen] = useState(false)
  const [currentSceneId, setCurrentSceneId] = useState('')
  const [currentPrompt, setCurrentPrompt] = useState('')
  const [currentNarration, setCurrentNarration] = useState('')
  const [currentImageId, setCurrentImageId] = useState(1)
  const [dialogImages, setDialogImages] = useState<string[]>([])

  // // 对话框操作状态 - 保留防重复操作逻辑
  // const dialogOperatingScenesRef = React.useRef<Set<string>>(new Set())

  // Prompt dialog state
  const [resolution, setResolution] = useState('864x1536')
  const [imageSizes, setImageSizes] = useState<string[]>([])
  const [quality, setQuality] = useState('standard')

  // Voice-related state
  const [voices, setVoices] = useState<{ voice_id: string; name: string }[]>([])
  const [selectedVoice, setSelectedVoice] = useState('')

  // ==================== 核心状态管理函数 ====================

  // 创建新的loading态分镜
  const createLoadingScene = () => {
    const newScene: SceneItem = {
      id: generateUniqueId(),
      desc: '',
      prompt: '',
      dialogue: '',
      images: [],
      videoUrl: '#',
      audioUrl: '#',
      status: SceneStatus.LOADING,
      cursorPosition: { startTime: 0, endTime: 5 },
      audioPlayState: false,
      videoLoading: true,
      waveformData: []
    }

    setSceneList(prev => [...prev, newScene])
    return newScene.id
  }

  // 更新分镜数据并转为ready状态
  const updateSceneReady = (sceneId: string, sceneData: Partial<SceneItem>) => {
    // 处理异步场景添加的情况：直接在setSceneList中更新
    setSceneList(prev => {
      // 1. 从prev状态中找到旧的场景数据（确保能找到最新添加的场景）
      const oldScene = prev.find(item => item.id === sceneId)
      if (!oldScene) {
        console.error(`[updateSceneReady] 在状态中找不到ID为 ${sceneId} 的场景`)
        return prev
      }

      // 2. 合并旧数据和新数据，得到最新的完整场景数据
      const newScene: SceneItem = {
        ...oldScene,
        ...sceneData,
        status: SceneStatus.READY,
        videoLoading: false
      }

      // 3. 返回更新后的场景列表
      const updatedList = prev.map(item =>
        item.id === sceneId ? newScene : item
      )

      // 4. 直接使用最新的完整数据调用媒体生成函数（无需等待React状态更新）
      console.log('[updateSceneReady] 立即触发媒体生成:', newScene)
      generateMediaForScene(sceneId, newScene, "updateSceneReady")

      return updatedList
    })
  }

  // 设置分镜为生成媒体状态
  const setSceneGeneratingMedia = (sceneId: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId
        ? { ...item, status: SceneStatus.GENERATING_MEDIA }
        : item
    ))
  }

  // 更新媒体生成结果
  const updateSceneMediaResult = (sceneId: string, mediaData: { images?: string[], audioUrl?: string, waveformData?: number[] }) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId
        ? {
          ...item,
          ...mediaData,
          status: SceneStatus.READY
        }
        : item
    ))
  }

  // 设置分镜错误状态 - 暂时不再使用，因为语音/图片生成失败不算场景失败
  // const setSceneError = (sceneId: string) => {
  //   setSceneList(prev => prev.map(item =>
  //     item.id === sceneId
  //       ? { ...item, status: SceneStatus.ERROR }
  //       : item
  //   ))
  // }

  // ==================== 游标位置管理功能 ====================

  // 更新游标位置（拖拽时只更新Ref，避免状态异步问题）
  const updateCursorPosition = (sceneId: string, type: 'start' | 'end', value: number, fromDrag: boolean = false) => {
    console.log('🎯 [updateCursorPosition] 更新游标位置:', { sceneId, type, value, isDragging: isDraggingRef.current, fromDrag });

    // 确保值在0-10范围内并保留两位小数
    const clampedValue = Math.max(0, Math.min(10, Number(value.toFixed(2))));

    // 先更新Ref
    const currentPosition = latestCursorPositionsRef.current[sceneId] || { startTime: 0, endTime: 5 };

    let newPosition: CursorPosition;

    if (type === 'start') {
      // 左侧游标（开始时间）移动时，右侧游标不动，只限制左侧不能超过右侧
      const newStartTime = clampedValue;
      // 确保开始时间不大于结束时间
      const actualStartTime = Math.min(newStartTime, currentPosition.endTime);

      newPosition = {
        startTime: actualStartTime,
        endTime: currentPosition.endTime // 保持右侧游标位置不变
      };
    } else {
      // 右侧游标（结束时间）移动时，直接调整结束时间，开始时间不变
      const newEndTime = clampedValue;
      // 确保结束时间不小于开始时间
      const actualEndTime = Math.max(currentPosition.startTime, newEndTime);

      newPosition = {
        startTime: currentPosition.startTime,
        endTime: actualEndTime
      };
    }

    // 只有当位置实际发生变化时才继续处理
    const hasChanged = currentPosition.startTime !== newPosition.startTime || currentPosition.endTime !== newPosition.endTime;
    if (!hasChanged) {
      return; // 没有变化，直接返回
    }

    // 更新Ref（保持最新的游标位置）
    const newCursorPositions = { ...latestCursorPositionsRef.current, [sceneId]: newPosition };
    latestCursorPositionsRef.current = newCursorPositions;

    // 🔧 关键修复：立即更新sceneList状态以确保拖拽响应性
    console.log('🎯 [updateCursorPosition] 立即更新sceneList状态，isDragging:', isDraggingRef.current);
    setSceneList(prev => {
      const updatedList = prev.map(item =>
        item.id === sceneId ? { ...item, cursorPosition: newPosition } : item
      );
      return updatedList;
    });
  }

  // 新增：同步游标位置到sceneList状态（用于在拖拽结束时调用）
  const syncCursorToSceneList = (sceneId: string) => {
    const latestPosition = latestCursorPositionsRef.current[sceneId];
    if (latestPosition) {
      console.log('🔄 [syncCursorToSceneList] 同步游标位置到状态:', { sceneId, position: latestPosition });
      setSceneList(prev => {
        const updatedList = prev.map(item =>
          item.id === sceneId ? { ...item, cursorPosition: latestPosition } : item
        );
        return updatedList;
      });
    }
  }

  // ==================== 音频播放管理功能 ====================

  // 切换音频播放状态
  const toggleAudioPlayState = (sceneId: string) => {
    setSceneList(prev => {
      const targetScene = prev.find(item => item.id === sceneId);
      if (!targetScene) return prev;

      // 检查是否有有效的音频URL
      if (!targetScene.audioUrl || targetScene.audioUrl === '#') {
        console.warn('🎵 没有有效的音频URL，无法播放:', sceneId);
        return prev;
      }

      const newPlayState = !targetScene.audioPlayState;

      // 获取或创建音频实例
      let audio = audioInstancesRef.current[sceneId];
      if (!audio) {
        audio = new Audio(targetScene.audioUrl);
        audioInstancesRef.current[sceneId] = audio;

        // 监听播放完成事件
        audio.addEventListener('ended', () => {
          console.log('🎵 音频播放完成:', sceneId);
          setSceneList(currentList =>
            currentList.map(item =>
              item.id === sceneId ? { ...item, audioPlayState: false } : item
            )
          );
        });

        // 监听播放错误
        audio.addEventListener('error', () => {
          console.error('🎵 音频加载错误:', sceneId);
          setSceneList(currentList =>
            currentList.map(item =>
              item.id === sceneId ? { ...item, audioPlayState: false } : item
            )
          );
        });
      }

      // 控制音频播放
      try {
        if (newPlayState) {
          audio.play();
          console.log('🎵 开始播放音频:', sceneId);
        } else {
          audio.pause();
          audio.currentTime = 0; // 重置到开头
          console.log('🎵 暂停音频:', sceneId);
        }
      } catch (error) {
        console.error('🎵 音频播放控制失败:', error);
        // 失败时重置状态
        return prev.map(item =>
          item.id === sceneId ? { ...item, audioPlayState: false } : item
        );
      }

      // 更新状态
      return prev.map(item =>
        item.id === sceneId ? { ...item, audioPlayState: newPlayState } : item
      );
    });
  }

  // ==================== 媒体生成功能 ====================

  // 为指定场景生成媒体（保留原有逻辑）
  const generateMediaForScene = (sceneId: string, scene: SceneItem, callLocation: string) => {
    console.log(`🎯 [${callLocation}] 开始为场景 ${sceneId} 生成媒体`)

    // 设置为生成中状态
    setSceneGeneratingMedia(sceneId)

    // 1. 生成图片
    if (scene.prompt && scene.prompt.trim() && scene.images.length === 0) {
      const imageRequestParams = {
        prompt: scene.prompt,
        image_size: resolution.replace(/\s*\([^)]+\)$/, ''), // 移除括号里的比例信息
        num_images: 1,
        quality: quality,
        style: 'realistic',
        watermark: false
      }

      const message = `正在生成图片...\n参数:\n提示词: ${imageRequestParams.prompt.slice(0, 20)}...\n分辨率: ${imageRequestParams.image_size}\n质量: ${imageRequestParams.quality}`
      setToastMessage(message)
      setToastVisible(true)

      generateImage(imageRequestParams)
        .then(response => {
          if (response.code === 200 && response.data?.image_urls?.length > 0) {
            updateSceneMediaResult(sceneId, { images: response.data.image_urls })
            setTimeout(() => saveSceneList(), 100)
          }
        })
        .catch(error => {
          console.error(`❌ [${callLocation}] 图片生成失败:`, error)
          // 图片生成失败，保持场景状态，不标记为ERROR
          // setSceneError(sceneId)
          // 可以选择将状态恢复为READY
          setSceneGeneratingMedia(sceneId); // 保持生成状态，或注释掉恢复到READY
        })
    }

    // 2. 生成语音
    if (scene.dialogue && scene.dialogue.trim() && scene.audioUrl === '#') {
      const ttsRequestParams = {
        text: scene.dialogue,
        voice_name: selectedVoice,
        audio_format: 'mp3' as const,
        volume: 1.0,
        speed: 1.0,
        emotion: 'neutral' as const,
        response_type: 'url' as const
      }

      generateTTS(ttsRequestParams)
        .then(response => {
          if (response.code === 200) {
            let audioUrl = response.data?.audio_url

            if (!audioUrl && response.data?.audio_base64) {
              try {
                const binaryString = atob(response.data.audio_base64)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i)
                }
                const blob = new Blob([bytes], { type: 'audio/mp3' })
                audioUrl = URL.createObjectURL(blob)
              } catch (error) {
                console.error('❌ base64音频解析失败:', error)
              }
            }

            if (audioUrl) {
              updateSceneMediaResult(sceneId, { audioUrl })
              generateWaveformData(audioUrl, sceneId)
            }
          }
        })
        .catch(error => {
          console.error(`❌ [${callLocation}] 语音生成失败:`, error)
          // 语音生成失败，保持场景状态，不标记为ERROR
          // setSceneError(sceneId)
          // 可以选择将状态恢复为READY
        })
    }
  }

  // ==================== 播放器重载功能 ====================

  // 重新加载播放器并从指定场景开始播放
  const reloadPlayerAndPlayFromScene = (sceneIndex: number = 0) => {
    console.log('🎬 [VideoPage] 重新加载播放器并从场景开始播放', { sceneIndex, hasPlayer: !!videoPlayerInstance.current });

    if (!videoPlayerInstance.current) {
      console.error('🎬 [VideoPage] 播放器实例不存在，无法重新加载');
      return;
    }

    if (sceneList.length === 0) {
      console.error('🎬 [VideoPage] 场景列表为空，无法重新加载播放器');
      return;
    }

    if (sceneIndex < 0 || sceneIndex >= sceneList.length) {
      console.error('🎬 [VideoPage] 场景索引超出范围', { sceneIndex, totalScenes: sceneList.length });
      return;
    }

    try {
      // 使用播放器的reloadToScene方法，直接调用以获得更好的控制
      console.log('📹 [VideoPage] 调用 reloadToScene，场景索引:', sceneIndex, 'autoPlay:', true, '当前场景列表长度:', sceneList.length);

      const newInstance = videoPlayerInstance.current.reloadToScene(
        sceneList,
        sceneIndex,
        true // 自动播放
      );

      console.log('📹 [VideoPage] reloadToScene 调用成功，已更新播放器实例');

      // 更新播放器实例引用
      videoPlayerInstance.current = newInstance;

      console.log('🎬 [VideoPage] 播放器重新加载成功，从场景', sceneIndex + 1, '开始播放');
    } catch (error) {
      console.error('🎬 [VideoPage] 重新加载播放器失败:', error);
    }
  };

  // 获取当前场景列表
  const getSceneList = () => {
    return sceneList;
  };

  // 通过ref暴露方法
  useImperativeHandle(ref, () => ({
    reloadPlayerAndPlayFromScene,
    getSceneList
  }), [videoPlayerInstance.current, sceneList]);

  // ==================== 对话框处理函数 ====================

  // 实时更新提示词（用于弹窗编辑时的实时同步）
  const handleUpdatePromptSilent = (sceneId: string, newPrompt: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, prompt: newPrompt } : item
    ))
  }

  const handleUpdatePrompt = (sceneId: string, newPrompt: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, prompt: newPrompt } : item
    ))
    saveSceneList()
  }

  // 实时更新旁白（用于弹窗编辑时的实时同步）
  const handleUpdateNarrationSilent = (sceneId: string, newNarration: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, dialogue: newNarration } : item
    ))
  }

  const handleUpdateNarration = (sceneId: string, newNarration: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, dialogue: newNarration, audioUrl: '#' } : item
    ))
    saveSceneList()
  }

  const handleUpdateImages = (sceneId: string, images: string[]) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, images } : item
    ))
    saveSceneList()
  }

  // ==================== 音频波形生成功能 ====================

  const generateWaveformData = async (audioUrl: string, sceneId: string) => {
    if (!audioUrl || audioUrl === "#") return

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const response = await fetch(audioUrl)
      const arrayBuffer = await response.arrayBuffer()
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
      const channelData = audioBuffer.getChannelData(0)

      const sampleSize = Math.max(1, Math.floor(channelData.length / 500))
      const sampledData: number[] = []

      for (let i = 0; i < channelData.length; i += sampleSize) {
        const sum = channelData.slice(i, i + sampleSize).reduce((a, b) => a + b, 0)
        const avg = sum / sampleSize
        sampledData.push(Math.abs(avg))
      }

      const maxValue = Math.max(...sampledData, 0.0001)
      const normalizedData = sampledData.map(value => value / maxValue)

      updateSceneMediaResult(sceneId, { waveformData: normalizedData })
    } catch (error) {
      console.error("Failed to generate waveform data:", error)
    }
  }

  // ==================== 保存功能 ====================

  const saveSceneList = async () => {
    if (!sceneList.length) return

    // 🔧 调试title值
    console.log('🔍 [saveSceneList] 当前title值:', `"${title}"`)

    // 🔧 关键修复：合并sceneList和latestCursorPositionsRef中的最新游标位置
    const mergedSceneList = sceneList.map(scene => {
      const latestCursorPosition = latestCursorPositionsRef.current[scene.id];
      if (latestCursorPosition) {
        console.log('🔧 [saveSceneList] 合并最新游标位置:', {
          sceneId: scene.id,
          oldPosition: scene.cursorPosition,
          newPosition: latestCursorPosition
        });
        return {
          ...scene,
          cursorPosition: latestCursorPosition
        };
      }
      return scene;
    });

    // 过滤掉不需要保存的字段
    const filteredSceneList = mergedSceneList.map(scene => {
      const { audioPlayState, videoLoading, waveformData, ...rest } = scene;
      return rest;
    });

    // 在保存的JSON外层增加title字段
    const savedData = {
      title: title,
      sceneList: filteredSceneList
    }

    const jsonData = JSON.stringify(savedData, null, 2)
    console.log('📄 [saveSceneList] 保存触发 - 包含title的完整数据:')
    console.log(jsonData)

    // ==== 3秒延时频控调用API保存到服务器 ====
    // 清除之前的定时器
    if (apiSaveTimerRef.current) {
      clearTimeout(apiSaveTimerRef.current);
    }

    // 设置新的3秒延时保存
    apiSaveTimerRef.current = setTimeout(async () => {
      try {
        console.log('🌐 [saveSceneList] 3秒延时调用API保存到服务器...')
        const response = await saveSceneListApi(savedData);
        if (response.code === 200) {
          console.log('✅ [saveSceneList] 服务器保存成功:', response.data);
        } else {
          console.error('❌ [saveSceneList] 服务器保存失败:', response.message);
        }
      } catch (error) {
        console.error('❌ [saveSceneList] API调用失败:', error);
      }
    }, 3000); // 3秒延时

    // ==== 保存后同步更新播放器 ====
    try {
      if (videoPlayerContainer.current && mergedSceneList.length > 0 && videoPlayerInstance.current) {
        // 找到当前场景的索引
        let startSceneIndex = 0;
        if (currentSceneId) {
          startSceneIndex = mergedSceneList.findIndex(scene => scene.id === currentSceneId);
          // 如果没找到，默认从0开始
          if (startSceneIndex === -1) {
            startSceneIndex = 0;
          }
        }

        // 获取当前播放器状态
        const currentPlaybackStatus = videoPlayerInstance.current.isPlaying;
        console.log('🎬 [saveSceneList] 保存后同步更新播放器，当前场景:', startSceneIndex, '播放状态:', currentPlaybackStatus);

        // 更新播放器 - 使用合并后的数据，确保包含最新的游标位置
        videoPlayerInstance.current = videoPlayerInstance.current.reloadToScene(
          mergedSceneList,
          startSceneIndex,
          currentPlaybackStatus
        );
      }
    } catch (error) {
      console.error('🎬 [saveSceneList] 更新播放器失败:', error);
    }
  }

  // 监听sceneList变化，初始化或更新播放器
  useEffect(() => {
    // 自动保存功能
    if (sceneList.length > 0 && stateMachine.pageState !== PageState.GENERATING && !isDraggingRef.current && !skipNextSaveRef.current) {
      const timer = setTimeout(() => {
        console.log('🔄 [useEffect] sceneList变化，自动保存')
        saveSceneList()
      }, 500)

      // 只在播放器实例不存在时才初始化 - 更新逻辑已移到saveSceneList中
      const initializePlayer = () => {
        if (videoPlayerContainer.current && sceneList.length > 0 && !videoPlayerInstance.current) {
          console.log('🎬 [useEffect] 初始化新的播放器实例');
          videoPlayerInstance.current = new PureFrontendVideoPlayer(videoPlayerContainer.current, sceneList);
        }
      }

      // 确保DOM渲染完成后初始化
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializePlayer)
      } else {
        initializePlayer()
      }

      return () => {
        clearTimeout(timer)
        document.removeEventListener('DOMContentLoaded', initializePlayer)
      }
    } else if (skipNextSaveRef.current) {
      // 重置标记
      skipNextSaveRef.current = false;
      console.log('🔄 [useEffect] 跳过本次自动保存');
    }
  }, [sceneList, currentSceneId])

  // 组件卸载时销毁播放器和音频实例
  useEffect(() => {
    return () => {
      if (videoPlayerInstance.current) {
        videoPlayerInstance.current.destroy()
        videoPlayerInstance.current = null
      }

      // 清理所有音频实例
      Object.values(audioInstancesRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      audioInstancesRef.current = {};

      // 清理API保存定时器
      if (apiSaveTimerRef.current) {
        clearTimeout(apiSaveTimerRef.current);
        apiSaveTimerRef.current = null;
      }
    }
  }, [])


  // 恢复输入框状态
  const restoreInputState = () => {
    if (stateMachine.getInputBarCompatState()) {
      // 保存当前sceneList
      saveSceneList();

      // 取消SSE请求
      if (sseReaderRef.current) {
        sseReaderRef.current.cancel('User clicked restart button.');
        sseReaderRef.current = null;
      }

      // 通知状态机停止生成，销毁播放器
      stateMachine.stopGeneration();

      // 重置状态
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = '100px'
        }
      }, 100)
      setSceneList([])
    }
  }

  // ==================== 删除分镜功能 ====================

  const handleDeleteScene = (sceneId: string) => {
    console.log('🗑️ [handleDeleteScene] 删除场景:', sceneId);

    setSceneList(prev => {
      const updatedList = prev.filter(item => item.id !== sceneId);
      console.log('🗑️ [handleDeleteScene] 删除后的场景列表长度:', updatedList.length);
      return updatedList;
    });

    // 同时从游标位置Ref中删除该场景的数据
    if (latestCursorPositionsRef.current[sceneId]) {
      delete latestCursorPositionsRef.current[sceneId];
      console.log('🗑️ [handleDeleteScene] 已清理Ref中的游标数据');
    }


    // 保存更改
    setTimeout(() => saveSceneList(), 100);
  }

  // ==================== 视频合成功能 ====================

  const handleVideoComposition = async () => {
    console.log('🎬 [handleVideoComposition] 开始处理视频合成');

    // 检查是否有场景数据
    if (!sceneList.length) {
      console.warn('⚠️ [handleVideoComposition] 场景列表为空，无法合成视频');
      alert('没有可用的场景数据，请先生成分镜内容');
      return;
    }

    // 检查是否有标题
    if (!title.trim()) {
      console.warn('⚠️ [handleVideoComposition] 视频标题为空');
      alert(t('videoEdit.noTitleError'));
      return;
    }

    try {
      // 🔧 关键修复：合并sceneList和latestCursorPositionsRef中的最新游标位置
      const mergedSceneList = sceneList.map(scene => {
        const latestCursorPosition = latestCursorPositionsRef.current[scene.id];
        if (latestCursorPosition) {
          console.log('🔧 [handleVideoComposition] 合并最新游标位置:', {
            sceneId: scene.id,
            oldPosition: scene.cursorPosition,
            newPosition: latestCursorPosition
          });
          return {
            ...scene,
            cursorPosition: latestCursorPosition
          };
        }
        return scene;
      });

      // 过滤掉不需要提交的字段，准备API数据
      const apiSceneList = mergedSceneList.map(scene => {
        const {
          audioPlayState,
          videoLoading,
          waveformData,
          status,
          ...apiScene
        } = scene;

        return {
          ...apiScene,
          status: 'ready', // API要求status为ready
          cursorPosition: apiScene.cursorPosition || { startTime: 0, endTime: 5 } // 确保cursorPosition始终有值
        };
      });

      // 准备提交给API的数据
      const videoTaskData = {
        title: title.trim(),
        sceneList: apiSceneList
      };

      // 📋 打印提交参数信息
      console.log('📋 [handleVideoComposition] 提交的视频合成参数:');
      console.log('标题:', videoTaskData.title);
      console.log('场景数量:', videoTaskData.sceneList.length);
      console.log('完整JSON数据:');
      console.log(JSON.stringify(videoTaskData, null, 2));

      // 显示提交中的提示
      const message = `${t('toast.common.processing')}\n${t('videoEdit.title')}: ${videoTaskData.title}\n${t('videoEdit.sceneCount', { count: videoTaskData.sceneList.length })}`;
      setToastMessage(message);
      setToastVisible(true);

      // 调用API提交视频合成任务
      const response = await createVideoTask(videoTaskData);

      if (response.code === 200 && response.data?.taskId) {
        const successMessage = `${t('videoEdit.taskSubmittedSuccessWithEmail')}\n${t('videoEdit.title')}: ${videoTaskData.title}`;
        setToastMessage(successMessage);
        setToastVisible(true);
        console.log('✅ [handleVideoComposition] 视频合成任务提交成功:', {
          taskId: response.data.taskId,
          title: videoTaskData.title
        });
      } else {
        throw new Error(response.message || t('videoEdit.taskSubmittedError'));
      }

    } catch (error) {
      console.error('❌ [handleVideoComposition] 视频合成任务提交失败:', error);
      const errorMessage = `${t('toast.common.error')} ${t('videoEdit.taskSubmittedError')}: ${error instanceof Error ? error.message : t('toast.common.unknownError')}`;
      setToastMessage(errorMessage);
      setToastType('error');
      setToastVisible(true);
    }
  }

  // ==================== 添加分镜功能 ====================

  // 处理替换第一张图片的函数
  const handleUpdateFirstImage = (sceneId: string, imageUrl: string) => {
    setSceneList(prev =>
      prev.map(scene => {
        if (scene.id === sceneId) {
          // 替换第一张图片，如果不存在则添加
          const newImages = [...scene.images];
          if (newImages.length > 0) {
            newImages[0] = imageUrl;
          } else {
            newImages.push(imageUrl);
          }
          return { ...scene, images: newImages };
        }
        return scene;
      })
    );

    // 保存更改
    setTimeout(() => saveSceneList(), 100);
  };

  const handleAddItemAfterId = (itemId: string) => {
    const newId = generateUniqueId()
    const newItem: SceneItem = {
      id: newId,
      desc: '',
      prompt: '',
      dialogue: '',
      images: [],
      videoUrl: '#',
      audioUrl: '#',
      status: SceneStatus.READY,
      cursorPosition: { startTime: 0, endTime: 5 },
      audioPlayState: false,
      videoLoading: false,
      waveformData: []
    }

    setSceneList(prev => {
      const insertAfterIndex = prev.findIndex(item => item.id === itemId)
      const newList = [...prev]
      if (insertAfterIndex === -1) {
        newList.push(newItem)
      } else {
        newList.splice(insertAfterIndex + 1, 0, newItem)
      }
      return newList
    })

    setTimeout(() => saveSceneList(), 0)
  }

  // ==================== 流式数据处理功能 ====================

  const streamChatRequest = async (content: string) => {
    console.log('[STATE_MACHINE] 🚀 streamChatRequest called, using state machine methods');

    // 使用状态机方法替代原有的状态设置
    stateMachine.startGeneration();

    setSceneList([])
    // 已使用状态机状态替代，无需单独设置

    // 🔧 重置当前项目ID，开始新项目
    resetCurrentProjectId()

    try {
      const response = await chatCompletion({
        messages: [{ role: 'user', content }],
        stream: true,
        promptId: 'video_storyboard',
        model: 'deepseek-chat'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No readable stream from response')
      }

      // 保存reader到ref，以便后续可以取消
      sseReaderRef.current = reader

      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      let accumulatedContent = ''

      // 解析状态
      let currentSceneId: string | null = null
      let currentSceneData: Partial<SceneItem> = {}

      function processContentLine(contentLine: string) {
        // console.log(`📝 [PROCESS_LINE] 处理内容行: "${contentLine}"`)
        if (!contentLine.trim()) return

        // 处理标题
        if (contentLine.includes('**title**:')) {
          // 查找title标记的位置，然后从该位置开始提取
          const titleIndex = contentLine.indexOf('**title**:')

          if (titleIndex !== -1) {
            // 从title标记位置后10个字符开始提取（即冒号后的内容）
            const parsedTitle = contentLine.slice(titleIndex + 10).trim()
            console.log('🎬 [PROCESS_TITLE] 解析到标题:', parsedTitle)
            console.log('🎬 [PROCESS_TITLE] 原始行:', `"${contentLine}"`)
            console.log('🎬 [PROCESS_TITLE] 提取结果:', `"${parsedTitle}"`)
            console.log('🎬 [PROCESS_TITLE] title标记位置:', titleIndex)
            setTitle(parsedTitle) // 保存标题
          }
          return
        }

        // 处理新场景开始标记
        if (contentLine.startsWith('## scene')) {
          // 创建新的loading态分镜
          currentSceneId = createLoadingScene()
          currentSceneData = {
            desc: '',
            prompt: '',
            dialogue: ''
          }
          return
        }

        // 处理场景字段
        if (currentSceneId) {
          if (contentLine.startsWith('**desc**:')) {
            const fieldValue = contentLine.slice(8).trim()
            currentSceneData.desc = fieldValue
          } else if (contentLine.startsWith('**prompt**:')) {
            const fieldValue = contentLine.slice(10).trim()
            currentSceneData.prompt = fieldValue
          } else if (contentLine.startsWith('**dialogue**:')) {
            const fieldValue = contentLine.slice(12).trim()
            currentSceneData.dialogue = fieldValue

            // dialogue完成，分镜数据完整
            if (currentSceneData.prompt && fieldValue) {
              console.log(`📝 [DIALOGUE_COMPLETION] 场景完整，更新状态，场景ID: ${currentSceneId}`)
              updateSceneReady(currentSceneId, currentSceneData)
            }
          } else {
            // 累积同一个字段的后续内容
            if (currentSceneData.desc && !contentLine.startsWith('**')) {
              currentSceneData.desc += '\n' + contentLine
            } else if (currentSceneData.prompt && !contentLine.startsWith('**')) {
              currentSceneData.prompt += '\n' + contentLine
            } else if (currentSceneData.dialogue && !contentLine.startsWith('**')) {
              currentSceneData.dialogue += '\n' + contentLine
            }
          }
        }
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        buffer += chunk

        const sseEvents = buffer.split(/\n\n/)
        buffer = sseEvents.pop() || ''

        for (const event of sseEvents) {
          if (!event.trim()) continue

          const eventLines = event.split('\n')
          let eventType = ''
          let eventData = ''

          for (const line of eventLines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim()
            } else if (line.startsWith('data:')) {
              eventData += line.slice(5).trim()
            }
          }

          if (eventType === 'message' && eventData) {
            try {
              const data = JSON.parse(eventData)
              if (data.message?.content) {
                const content = data.message.content
                if (!content) continue

                accumulatedContent += content
                const contentLines = accumulatedContent.split('\n')
                accumulatedContent = contentLines.pop() || ''

                for (const contentLine of contentLines) {
                  processContentLine(contentLine)
                }
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', parseError)
            }
          }
        }
      }

      // 处理缓冲区中剩余的内容
      if (accumulatedContent.trim()) {
        const contentLines = accumulatedContent.split('\n')
        for (const contentLine of contentLines) {
          processContentLine(contentLine)
        }
      }

    } catch (error) {
      console.error('Chat request failed:', error)
      alert('对话请求失败: ' + (error instanceof Error ? error.message : '未知错误'))
      // 使用状态机方法替代直接的状态设置
      stateMachine.showError()
    } finally {
      // 关闭流标记并保存分镜列表
      // 已使用状态机状态替代，无需单独设置
      // 清理reader
      sseReaderRef.current = null;
      setTimeout(() => saveSceneList(), 300);
      // 确保状态机的加载状态被重置

      // 🔴 关键修复：SSE结束时将状态切换为EDITING
      // 完全使用状态机控制，如果当前状态不允许转换（比如用户已经手动停止了），
      // transitionTo会自动拒绝转换，并打印警告信息。
      stateMachine.completeGeneration();
    }
  }

  // 组件挂载时获取图片尺寸
  useEffect(() => {
    const fetchImageSizes = async () => {
      try {
        const response = await getImageSizes()
        if (response.code === 200 && response.data?.sizes) {
          setImageSizes(response.data.sizes)
          if (response.data.sizes.length > 0 && !response.data.sizes.includes(resolution)) {
            setResolution(response.data.sizes[0])
          }
        } else {
          setImageSizes(['1024x1024', '1024x1364', '864x1536', '1364x1024'])
        }
      } catch (error) {
        setImageSizes(['1024x1024', '1024x1364', '864x1536', '1364x1024'])
      }
    }
    fetchImageSizes()
  }, [])

  // 组件挂载时获取音色列表
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await getVoices()
        const responseData = response as any

        let foundVoices: Array<{ voice_id: string; name: string }> = []

        if (Array.isArray(responseData)) {
          foundVoices = responseData
        } else if (responseData.voices) {
          foundVoices = responseData.voices
        } else if (responseData.data?.voices) {
          foundVoices = responseData.data.voices
        } else if (responseData.data?.data?.voices) {
          foundVoices = responseData.data.data.voices
        }

        if (foundVoices.length > 0) {
          const formattedVoices = foundVoices
            .filter((voice: any) => voice.voice_id && voice.name)
            .map((voice: any) => ({
              voice_id: voice.voice_id,
              name: voice.name
            }))
          setVoices(formattedVoices)
          // 如果当前没有选中的语音，或者选中的语音不在列表中，则选择第一个
          if (!selectedVoice || !formattedVoices.find(v => v.voice_id === selectedVoice)) {
            setSelectedVoice(formattedVoices[0].voice_id)
          }
        } else {
          // 接口失败时使用空数组
          setVoices([])
          setSelectedVoice('')
        }
      } catch (error) {
        console.error('获取语音列表失败:', error)
        // 异常时使用空数组
        setVoices([])
        setSelectedVoice('')
      }
    }
    fetchVoices()
  }, [])

  // 监听当前操作的场景图片变化，更新对话框预览
  useEffect(() => {
    if ((isTextToImageDialogOpen || isImageSelectionDialogOpen) && currentSceneId) {
      const currentScene = sceneList.find(item => item.id === currentSceneId)
      if (currentScene) {
        setDialogImages(currentScene.images)
      }
    }
  }, [sceneList.find(item => item.id === currentSceneId)?.images, isTextToImageDialogOpen, isImageSelectionDialogOpen, currentSceneId])


  // ==================== UI组件定义 ====================


  // 完整分镜卡片组件
  const SceneCard = ({ data, index, disabled = false }: { data: SceneItem; index: number; disabled?: boolean }) => (
    <div className={`w-full min-w-[500px] max-w-[700px] flex flex-col flex-shrink-0 transition-all duration-300 ${disabled ? 'opacity-60' : 'opacity-100'
      }`}>
      <div className="relative flex items-center" tabIndex={0}>
        <div className="absolute top-4 left-[-30px] text-xl font-bold text-white z-10">
          #{index + 1}
        </div>

        {/* 删除按钮与卡片内容垂直居中对齐 */}
        {stateMachine.pageState !== PageState.GENERATING && (
          <button
            className="absolute top-1/2 left-[-30px] transform -translate-y-1/2 w-6 h-6 rounded-full bg-gray-500/30 hover:bg-gray-500/50 text-gray-400 hover:text-gray-300 flex items-center justify-center transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              if (disabled) return;
              handleDeleteScene(data.id);
            }}
            disabled={disabled}
            title={t('videoEdit.deleteScene')}
          >
            <X className="w-3 h-3" />
          </button>
        )}



        <div className="flex flex-1 flex-col gap-3 rounded-xl p-4 border border-gray-700/50 h-fit min-h-[50px] focus-within:border-blue-400/70">
          <div className="flex flex-row gap-2">
            {/* Sence部分 */}
            <div className="flex-[10] flex-shrink-0 flex flex-col gap-2 m-1 justify-center">
              <div
                className="bg-gray-700/30 rounded-lg p-3 text-sm cursor-pointer hover:bg-gray-700/50 transition-colors"
                onClick={() => {
                  if (disabled) return
                  setCurrentPrompt(data.prompt)
                  setCurrentSceneId(data.id)
                  setIsTextToImageDialogOpen(true)
                }}
              >
                <div className="text-xs text-gray-400 mb-1">提示词</div>
                <div className="text-white text-sm/80 line-clamp-2 overflow-hidden">
                  {data.prompt}
                </div>
              </div>

              <div
                className="bg-gray-700/30 rounded-lg p-3 text-sm flex items-center cursor-pointer hover:bg-gray-700/50 transition-colors"
                onClick={() => {
                  if (disabled) return
                  setCurrentNarration(data.dialogue)
                  setCurrentSceneId(data.id)
                  setIsVoiceSynthesisDialogOpen(true)
                }}
              >
                <div className="flex-1">
                  <div className="text-xs text-gray-400 mb-1">旁白</div>
                  <div className="text-white">
                    {data.dialogue}
                  </div>
                </div>

                <div className="ml-1 mr-1 flex-shrink-0">
                  <button
                    className={`aspect-square w-10 h-10 rounded-full flex items-center justify-center transition-all ${disabled
                      ? 'bg-gray-600/50 cursor-not-allowed'
                      : 'bg-gray-500/30 hover:bg-gray-500/50 text-gray-400 hover:text-gray-300'
                      }`}
                    onClick={(e) => {
                      e.stopPropagation(); // 阻止事件冒泡到父级div
                      if (disabled) return
                      toggleAudioPlayState(data.id)
                    }}
                    disabled={disabled}
                  >
                    {data.audioPlayState ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* 游标进度条 */}
              <div className="mt-3">
                <div className="relative w-full h-4 bg-gray-700/30 rounded-full">
                  <div
                    className="absolute inset-y-0 bg-gray-600/50 rounded-full"
                    style={{
                      left: `calc(${((data.cursorPosition?.startTime || 0) / 10) * 100}% - 8px)`,
                      width: `calc(${(((data.cursorPosition?.endTime || 5) - (data.cursorPosition?.startTime || 0)) / 10) * 100}% + 16px)`
                    }}
                  />

                  {/* 开始游标 */}
                  <div
                    className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-blue-500/70 hover:bg-blue-500/90 rounded-full cursor-move transition-all duration-200 z-30 border border-white/30 group"
                    style={{ left: `${((data.cursorPosition?.startTime || 0) / 10) * 100}%` }}
                    title="开始时间"
                    onMouseDown={(e) => {
                      if (disabled) return
                      e.preventDefault();
                      console.log('🟢 [cursor] 开始游标 mousedown - 场景ID:', data.id, '开始拖拽');

                      // 设置拖拽状态
                      isDraggingRef.current = true;

                      const cursor = e.currentTarget;
                      const progressBar = cursor.parentElement as HTMLElement;
                      const rect = progressBar.getBoundingClientRect();
                      const tooltip = cursor.querySelector('div:last-child') as HTMLElement;

                      let hasChanged = false;

                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        moveEvent.preventDefault();
                        const x = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
                        const percentage = x / rect.width;
                        const value = percentage * 10;
                        const roundedValue = Math.max(0, Math.min(10, Number(value.toFixed(2))));

                        // 从本地 Ref 获取最新位置，避免状态异步问题
                        const localCursor = latestCursorPositionsRef.current[data.id] || { startTime: 0, endTime: 5 };
                        const currentValue = localCursor.startTime;

                        // Only update if value has changed by at least 0.05s to further reduce frequency
                        if (Math.abs(currentValue - roundedValue) > 0.05) {
                          hasChanged = true;
                          console.log('🟢 [cursor] 开始游标移动变化:', {
                            sceneId: data.id,
                            oldValue: currentValue,
                            newValue: roundedValue,
                            hasChanged: true
                          });

                          // Real-time update tooltip
                          tooltip.innerHTML = `字幕: ${roundedValue.toFixed(1)}s`;
                          tooltip.className = tooltip.className.replace(/bg-\w+-\d+/g, '').replace(/text-white/g, 'text-blue-500/90');

                          updateCursorPosition(data.id, 'start', roundedValue);
                        }
                      };

                      const handleMouseUp = (upEvent: MouseEvent) => {
                        console.log('🟢 [cursor] document handleMouseUp 触发:', {
                          sceneId: data.id,
                          hasChanged: hasChanged,
                          mouseTarget: upEvent.target,
                          mouseTargetClassName: (upEvent.target as HTMLElement)?.className,
                          isDragging: isDraggingRef.current
                        });

                        upEvent.preventDefault();
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);

                        // 清除拖拽状态
                        isDraggingRef.current = false;

                        // 只有当鼠标在游标上松开且有变化时才保存
                        if (hasChanged) {
                          console.log('🟢 [cursor] document handleMouseUp 满足保存条件，同步游标位置并保存');

                          // 🔧 关键修复：先同步最新的游标位置到sceneList状态
                          syncCursorToSceneList(data.id);

                          // 立即保存，用户完成操作（清除现有延时）
                          if (cursorSaveTimerRef.current) {
                            clearTimeout(cursorSaveTimerRef.current);
                            cursorSaveTimerRef.current = null;
                          }
                          setTimeout(() => saveSceneList(), 100);
                        } else {
                          console.log('🟢 [cursor] document handleMouseUp 无变化，不保存');
                        }
                      };

                      // Add listeners to document
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  >
                    <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs font-medium text-blue-500/90 whitespace-nowrap transition-all duration-200 opacity-90 hover:opacity-100">
                      字幕: {(data.cursorPosition?.startTime || 0).toFixed(1)}s
                    </div>
                  </div>

                  {/* 结束游标 */}
                  <div
                    className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-purple-500/70 hover:bg-purple-500/90 rounded-full cursor-move transition-all duration-200 z-30 border border-white/30 group"
                    style={{ left: `${((data.cursorPosition?.endTime || 5) / 10) * 100}%` }}
                    title={t('videoEdit.endTime')}
                    onMouseDown={(e) => {
                      if (disabled) return
                      e.preventDefault();
                      console.log('🔴 [cursor] 结束游标 mousedown - 场景ID:', data.id, '开始拖拽');

                      // 设置拖拽状态
                      isDraggingRef.current = true;

                      const cursor = e.currentTarget;
                      const progressBar = cursor.parentElement as HTMLElement;
                      const rect = progressBar.getBoundingClientRect();
                      const tooltip = cursor.querySelector('div:last-child') as HTMLElement;

                      let hasChanged = false;

                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        moveEvent.preventDefault();
                        const x = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
                        const percentage = x / rect.width;
                        const value = percentage * 10;
                        const roundedValue = Math.max(0, Math.min(10, Number(value.toFixed(2))));

                        // 从本地 Ref 获取最新位置，避免状态异步问题
                        const localCursor = latestCursorPositionsRef.current[data.id] || { startTime: 0, endTime: 5 };
                        const currentValue = localCursor.endTime;

                        // Only update if value has changed by at least 0.05s to further reduce frequency
                        if (Math.abs(currentValue - roundedValue) > 0.05) {
                          hasChanged = true;
                          console.log('🔴 [cursor] 结束游标移动变化:', {
                            sceneId: data.id,
                            oldValue: currentValue,
                            newValue: roundedValue,
                            hasChanged: true
                          });

                          // Real-time update tooltip
                          tooltip.innerHTML = `${roundedValue.toFixed(1)}s`;
                          tooltip.className = tooltip.className.replace(/bg-\w+-\d+/g, '').replace(/text-white/g, 'text-purple-500/90');

                          updateCursorPosition(data.id, 'end', roundedValue);
                        }
                      };

                      const handleMouseUp = (upEvent: MouseEvent) => {
                        console.log('🔴 [cursor] document handleMouseUp 触发:', {
                          sceneId: data.id,
                          hasChanged: hasChanged,
                          mouseTarget: upEvent.target,
                          mouseTargetClassName: (upEvent.target as HTMLElement)?.className,
                          isDragging: isDraggingRef.current
                        });

                        upEvent.preventDefault();
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);

                        // 清除拖拽状态
                        isDraggingRef.current = false;

                        // 只有当鼠标在游标上松开且有变化时才保存
                        if (hasChanged) {
                          console.log('🔴 [cursor] document handleMouseUp 满足保存条件，同步游标位置并保存');

                          // 🔧 关键修复：先同步最新的游标位置到sceneList状态
                          syncCursorToSceneList(data.id);

                          // 设置跳过标记，避免useEffect重复保存
                          skipNextSaveRef.current = true;

                          // 立即保存，用户完成操作（清除现有延时）
                          if (cursorSaveTimerRef.current) {
                            clearTimeout(cursorSaveTimerRef.current);
                            cursorSaveTimerRef.current = null;
                          }
                          setTimeout(() => saveSceneList(), 100);
                        } else {
                          console.log('🔴 [cursor] document handleMouseUp 无变化，不保存');
                        }
                      };

                      // Add listeners to document
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  >
                    <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs font-medium text-purple-500/90 whitespace-nowrap transition-all duration-200 opacity-90 hover:opacity-100">
                      {(data.cursorPosition?.endTime || 5).toFixed(1)}s
                    </div>
                  </div>
                </div>

                {/* 时间刻度 */}
                <div className="flex justify-between mt-1 text-xs text-gray-400">
                  {[0, 2, 4, 6, 8, 10].map(time => (
                    <div key={time}>{time}s</div>
                  ))}
                </div>
              </div>
            </div>

            {/* Image部分 */}
            <div className="flex-[5] flex-shrink-0 flex flex-col justify-center items-center m-1">
              <div className="w-full">
                <div className="bg-gray-700/30 rounded-lg flex items-center justify-center p-2">
                  {data.images.length > 0 ? (
                    <img
                      src={data.images[0]}
                      alt={`分镜 ${data.id} 图片 1`}
                      className="w-full aspect-[1/1] rounded object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => {
                        if (disabled) return
                        setCurrentSceneId(data.id)
                        setCurrentImageId(1)
                        setIsImageSelectionDialogOpen(true)
                      }}
                    />
                  ) : (
                    <div
                      className="w-full aspect-[1/1] bg-gray-700/30 rounded flex items-center justify-center text-xs text-gray-300 cursor-pointer hover:text-blue-400 transition-colors"
                      onClick={() => {
                        if (disabled) return
                        setCurrentSceneId(data.id)
                        setCurrentImageId(1)
                        setIsImageSelectionDialogOpen(true)
                      }}
                    >
                      图片 1
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 分镜分隔线 */}
      <div className="relative w-full mt-6">
        <div className="absolute inset-0 flex items-center">
          <div className="flex-1 border-t border-gray-700/50"></div>
          {stateMachine.pageState !== PageState.GENERATING && (
            <button
              className="mx-6 px-[10px] py-[6px] bg-gray-600/30 hover:bg-gray-600/50 text-gray-400 hover:text-gray-300 rounded-full text-sm font-medium transition-all flex items-center justify-center min-w-[32px] min-h-[32px]"
              onClick={() => handleAddItemAfterId(data.id)}
              title={t('videoEdit.addNewScene')}
              disabled={disabled}
            >
              +
            </button>
          )}
          <div className="flex-1 border-t border-gray-700/50"></div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="relative w-full h-[calc(100vh-110px)] rounded-xl shadow-2xl overflow-hidden flex flex-col">

      <div id="main-content-container" className="flex-1 flex items-center py-[4px] h-full">
        {/* Tutorial 区域 */}
        {stateMachine.pageState === PageState.INITIAL && (
          <div id="tutorial-area" className="w-full flex items-start justify-center p-8 overflow-y-auto">
            <div className="max-w-2xl bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">🎬 开始生成你的视频</h2>
                <p className="text-gray-300 text-sm">在下方输入框中描述你的视频创意，AI将自动为你生成分镜脚本</p>
              </div>
              <div className="bg-gray-800/95 rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://via.placeholder.com/800x400?text=视频创意示例"
                  alt="视频创意示例"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="mt-6 text-center text-sm text-gray-400">
                <p>示例："帮我生成一个关于太空探索的科幻短视频脚本，包含3个分镜"</p>
              </div>
            </div>
          </div>
        )}

        {/* 分镜列表区域 */}
        {(sceneList.length > 0 || stateMachine.getInputBarCompatState()) && (
          <div id="scene-list-container" className="flex-1 min-w-[600px] w-[800px] flex flex-col h-full items-start">
            <div className="flex-1 overflow-y-auto px-[20px] pl-[40px] scrollbar-hidden">
              <div className="ml-2 mr-2 space-y-6 pt-6 pb-4">
                {/* 完整的分镜卡片渲染逻辑 */}
                {sceneList.map((item, index) => {
                  switch (item.status) {
                    case SceneStatus.LOADING:
                      // Loading态使用简化版本，不包含波形和游标
                      return (
                        <div key={item.id} className="w-full min-w-[500px] max-w-[700px] flex flex-col flex-shrink-0">
                          <div className="relative flex items-center">
                            <div className="absolute top-4 left-[-30px] text-xl font-bold text-white z-10">#{index + 1}</div>

                            {/* 删除按钮与卡片内容垂直居中对齐 */}
                            {stateMachine.pageState !== PageState.GENERATING && (
                              <button
                                className="absolute top-1/2 left-[-30px] transform -translate-y-1/2 w-6 h-6 rounded-full bg-gray-500/30 hover:bg-gray-500/50 text-gray-400 hover:text-gray-300 flex items-center justify-center transition-colors z-10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteScene(item.id);
                                }}
                                title={t('videoEdit.deleteScene')}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                            <div className="flex flex-1 flex-col gap-3 rounded-xl p-2 border border-gray-700/50">
                              <div className="flex flex-row gap-4">
                                <div className="flex-[10] flex-shrink-0 flex flex-col gap-2 m-1">
                                  <div className="bg-gray-700/30 rounded-lg p-3">
                                    <div className="text-xs text-gray-400 mb-1">提示词</div>
                                    <div className="h-10 bg-gray-600/50 rounded animate-pulse-strong"></div>
                                  </div>
                                  <div className="bg-gray-700/30 rounded-lg p-3">
                                    <div className="text-xs text-gray-400 mb-1">旁白</div>
                                    <div className="h-6 bg-gray-600/50 rounded animate-pulse-strong"></div>
                                  </div>
                                </div>
                                <div className="flex-[5] flex-shrink-0">
                                  <div className="bg-gray-700/30 rounded-lg p-2">
                                    <div className="w-full aspect-[1/1] bg-gray-600/50 rounded animate-pulse-strong"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    case SceneStatus.GENERATING_MEDIA:
                      return (
                        <div key={item.id} className="w-full min-w-[500px] max-w-[700px] flex flex-col flex-shrink-0 relative">
                          <div className="absolute inset-0 z-10 bg-blue-500/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <div className="text-white text-lg font-medium">
                              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mx-auto mb-2"></div>
                              生成媒体中...
                            </div>
                          </div>
                          <SceneCard data={item} index={index} disabled={true} />
                        </div>
                      )
                    case SceneStatus.ERROR:
                      // 去掉错误提示红框，保持场景卡片可交互
                      return <SceneCard key={item.id} data={item} index={index} />
                    case SceneStatus.READY:
                    default:
                      return <SceneCard key={item.id} data={item} index={index} />
                  }
                })}

                {/* 显示下一个加载中的分镜 - 仅在流式进行且无当前loading分镜时显示 */}
                {stateMachine.pageState === PageState.GENERATING && !sceneList.some(item => item.status === SceneStatus.LOADING) && (
                  <div key="loading-next" className="w-full min-w-[500px] max-w-[700px] flex flex-col flex-shrink-0">
                    <div className="relative flex items-center">
                      <div className="absolute top-4 left-[-35px] text-xl font-bold text-white z-10">
                        #{sceneList.length + 1}
                      </div>
                      <div className="flex flex-1 flex-col gap-3 rounded-xl p-2 border border-gray-700/50">
                        <div className="flex flex-row gap-4">
                          <div className="flex-[10] flex-shrink-0 flex flex-col gap-2 m-1">
                            <div className="bg-gray-700/30 rounded-lg p-3">
                              <div className="text-xs text-gray-400 mb-1">提示词</div>
                              <div className="h-10 bg-gray-600/50 rounded animate-pulse-strong"></div>
                            </div>
                            <div className="bg-gray-700/30 rounded-lg p-3">
                              <div className="text-xs text-gray-400 mb-1">旁白</div>
                              <div className="h-6 bg-gray-600/50 rounded animate-pulse-strong"></div>
                            </div>
                          </div>
                          <div className="flex-[5] flex-shrink-0">
                            <div className="bg-gray-700/30 rounded-lg p-2">
                              <div className="w-full aspect-[1/1] bg-gray-600/50 rounded animate-pulse-strong"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 加载状态提示 */}
                {stateMachine.pageState === 'generating' && sceneList.length === 0 && (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-white text-xl">AI正在生成分镜...</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 左右侧分隔线 */}
        {sceneList.length > 0 && (
          <div className="w-px bg-gray-700/50 self-stretch mx-2"></div>
        )}

        {/* 右侧结果区域 */}
        {sceneList.length > 0 && (
          <div id="result-area-container" className="flex-[1] min-w-[300px] mx-8 flex flex-col items-center justify-center gap-4 px-2">
            {/* 标题 */}
            <h3 className="text-lg font-medium text-white/80">场景播放预览</h3>

            <div className="w-full max-w-[400px] aspect-square bg-gray-600/10 backdrop-blur-sm border border-gray-700/10 rounded-lg overflow-hidden relative" ref={videoPlayerContainer}>
              {/* 默认图片图标 */}
              <DefaultImageIcon className="w-full h-full" />
              {/* 纯前端播放器将在此处渲染，会覆盖默认图标 */}
            </div>
            <div className="flex gap-4 w-full justify-center">
              <button
                className="flex-1 max-w-[150px] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md border border-gray-700/50 rounded-lg px-4 py-3 text-white font-medium hover:shadow-lg transition-all"
                onClick={handleVideoComposition}
              >
                合成<br />视频
              </button>
            </div>

            {/* 播放器重载测试按钮 */}
            {sceneList.length > 0 && (
              <div className="w-full mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50">
                <div className="text-xs text-gray-400 mb-2">播放器重载测试:</div>
                <div className="flex flex-wrap gap-2">
                  {sceneList.map((scene, index) => (
                    <button
                      key={scene.id}
                      onClick={() => reloadPlayerAndPlayFromScene(index)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                      title={`从场景${index + 1}重新开始播放`}
                    >
                      场景{index + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 底部输入栏 inputBar - 完整版本 */}
      <div
        id="input-bar-container"
        className={`fixed bottom-[10px] left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl shadow-2xl ${stateMachine.getInputBarCompatState() ? 'flex items-center justify-center w-auto h-auto px-[10px] py-[5px] cursor-pointer' : 'w-[600px] h-[600px] p-0'}`}
        onClick={restoreInputState}
      >
        {/* 输入框容器 - 初始输入态 */}
        <div
          id="input-container-initial"
          className={`relative ${stateMachine.getInputBarCompatState() ? 'w-0 h-0 p-0 opacity-0 scale-95 translate-y-8 pointer-events-none' : 'w-[600px] h-[600px] p-4 opacity-100 scale-100 translate-y-0 pointer-events-auto'} bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl shadow-2xl transition-all duration-500`}
        >
          {/* 文本输入框和发送按钮容器 */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              className={`w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-xl px-6 py-3 pr-20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none mb-4`}
              placeholder={t('home.placeholder')}
              rows={1}
              onChange={(e) => {
                setInputContent(e.target.value)
              }}
              value={inputContent}
              style={{
                minHeight: '100px',
                maxHeight: '120px'
              }} // 缩小时1行，未缩小时3行
              onClick={restoreInputState} // 点击恢复状态
            />

            {/* 圆形发送按钮 */}
            <button
              className={`absolute right-4 bottom-8 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-300 scale-105 opacity-100`}
              onClick={(e) => {
                e.stopPropagation(); // 防止触发容器的restoreInputState
                if (stateMachine.getInputBarCompatState()) {
                  // 🔴 点击停止按钮时保存当前sceneList
                  saveSceneList();

                  // 🔴 关键修复：取消SSE请求
                  if (sseReaderRef.current) {
                    sseReaderRef.current.cancel('User clicked stop button.');
                    sseReaderRef.current = null;
                  }

                  // 🔴 关键修复：先通知状态机停止生成，销毁播放器
                  stateMachine.stopGeneration();
                  // 🔴 再重置sceneList，确保回到初始状态
                  setSceneList([]);
                } else if (inputContent.trim()) {
                  // 发送请求
                  streamChatRequest(inputContent.trim());
                }
              }}
            >
              {stateMachine.pageState === 'generating' ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* 配置选项 - 比例、分辨率、音色 */}
          <div
            className={`grid grid-cols-1 md:grid-cols-[3fr_3fr_3fr_2fr] gap-4 transition-all duration-300 opacity-100 max-h-40`}
          >
            {/* 质量选择 */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">质量</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                value={quality}
                onChange={(e) => setQuality(e.target.value)}
              >
                <option value="standard">标准 (standard)</option>
                <option value="hd">高清 (hd)</option>
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>

            {/* 分辨率选择 */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">分辨率</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                disabled={imageSizes.length === 0}
              >
                {imageSizes.length > 0 ? (
                  imageSizes.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))
                ) : (
                  <option value="loading">加载中...</option>
                )}
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>

            {/* 音色选择 */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">音色</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                disabled={voices.length === 0}
              >
                {voices.length > 0 ? (
                  voices.map(voice => (
                    <option key={voice.voice_id} value={voice.voice_id}>
                      {voice.name}
                    </option>
                  ))
                ) : (
                  <option value="loading">加载中...</option>
                )}
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">分镜数</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
              >
                <option value="auto">自动</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>
          </div>
        </div>

        {/* 发送后状态 - 旋转动画和重新开始按钮 */}
        <div
          id="input-container-sent"
          className={`flex items-center w-auto h-auto justify-center gap-2 ${stateMachine.getInputBarCompatState() ? 'px-[10px] py-[5px]' : 'p-[10px]'} transition-all duration-500 ${stateMachine.getInputBarCompatState() ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        >
          {/* 旋转动画 */}
          <div className={`w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin ${stateMachine.inputBarConfig.state === 'editing' ? 'opacity-0 invisible' : ''}`}></div>

          {/* 重新开始按钮 */}
          <button
            className="text-blue-400 hover:text-blue-300 font-medium text-xs transition-colors duration-200"
            onClick={restoreInputState}
          >
            重新开始 -&gt;
          </button>
        </div>
      </div>

      {/* 文生图对话框 */}
      <TextToImageDialog
        isOpen={isTextToImageDialogOpen}
        onClose={() => setIsTextToImageDialogOpen(false)}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        initialPrompt={currentPrompt}
        initialResolution={resolution}
        initialQuality={quality}
        imageSizes={imageSizes}
        onUpdatePrompt={handleUpdatePrompt}
        onUpdateImages={handleUpdateImages}
        onUpdatePromptSilent={handleUpdatePromptSilent}
        onUpdateResolution={(newResolution) => setResolution(newResolution)}
        onUpdateQuality={(newQuality) => setQuality(newQuality)}
        onSetDialogOperating={() => { }}
        onShowToast={(message) => {
          setToastMessage(message)
          setToastVisible(true)
        }}
        currentImages={dialogImages}
        sceneList={sceneList}
        onUpdateFirstImage={handleUpdateFirstImage}
      />

      {/* 语音合成对话框 */}
      <VoiceSynthesisDialog
        isOpen={isVoiceSynthesisDialogOpen}
        onClose={() => setIsVoiceSynthesisDialogOpen(false)}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        initialNarration={currentNarration}
        initialVoice={selectedVoice}
        voices={voices}
        onUpdateNarration={handleUpdateNarration}
        onUpdateAudio={(sceneId, audioUrl, duration) => {
          setSceneList(prev => prev.map(item => {
            if (item.id === sceneId) {
              // 更新音频URL
              const updatedScene = { ...item, audioUrl };

              // 如果有duration，更新进度条的结束时间
              if (duration && duration > 0) {
                updatedScene.cursorPosition = {
                  startTime: item.cursorPosition?.startTime || 0,
                  endTime: Math.min(duration, 10) // 限制最大时长为10秒
                };
              }

              return updatedScene;
            }
            return item;
          }))
          saveSceneList()
          generateWaveformData(audioUrl, sceneId)
        }}
        onUpdateNarrationSilent={handleUpdateNarrationSilent}
        onUpdateVoice={(newVoice) => setSelectedVoice(newVoice)}
        onSetDialogOperating={() => { }}
        onShowToast={(message) => {
          setToastMessage(message)
          setToastVisible(true)
        }}
      />

      {/* 图片选择对话框 */}
      <ImageSelectionDialog
        isOpen={isImageSelectionDialogOpen}
        onClose={() => setIsImageSelectionDialogOpen(false)}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        imageId={currentImageId}
        scene={sceneList.find(item => item.id === currentSceneId) || null}
      />

      {/* Toast */}
      {toastVisible && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastVisible(false)}
        />
      )}
    </div>
  )
});

VideoPage.displayName = 'VideoPage';
export default VideoPage;